import { ChangeEvent, FC, FocusEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { applyDictionaryToString } from '@belong/contentful';
import { DATE_FORMAT } from '@belong/date-utils';
import { IDictionary, IOnInputError, ITestable, TError, TMediaMap, TWidths } from '@belong/types';
import { ErrorValidation, getValidationSchema } from '@belong/validation';
import { Copy } from '../../styles/Typography/Copy';
import { DateInput, IDateInput } from '../DateInput';
import { Error } from '../Error';
import { type IInputInline, InputInline } from '../InputInline';
import { InputWrapper } from '../InputWrapper';
import { Label } from '../Label';
import { IUseForm } from '../useForm/useForm';

export interface IGenericDateInput
  extends Omit<IDateInput, 'name' | 'required' | 'id' | 'maxLength'>, IInputInline, ITestable {
  name: string;
  label: string;
  description?: string;
  dictionary?: IDictionary;
  width?: TWidths | TMediaMap;
  initialValue?: string;

  hasFormError?: boolean;
  /**
   * If there is an error related to this field outside of this component, we need to pass in the id of the error so it can be related to it
   */
  formErrorId?: string;
  manageForm: IUseForm['manageForm'];
  onClear?: IDateInput['onClear'];
  onInputError?: IOnInputError;
  /**
   * A list of field validations, if an empty list is provided, the field is not required and any input can be entered
   */
  errorValidation: ErrorValidation[];
}

/**
 * A generic date input field. It validates based on the `errorValidation` rules. It handles form errors when implemented with the useForm hook
 *
 * Note: for a field to be required you must provide include an `errorValidation` with the `errorKey` `required`
 */
export const GenericDateInput: FC<IGenericDateInput> = ({
  name,
  label,
  description,
  dictionary = {},
  width,
  initialValue,
  inlineElement,
  hasFormError = false,
  formErrorId = '',
  manageForm,
  format = DATE_FORMAT.INPUT,
  onClear,
  onInputError,
  errorValidation,
  showCalendarIcon = true,
  'data-testid': testid = name,
  onChange,
  ...extraProps
}) => {
  const [error, setError] = useState<TError>({ hasError: hasFormError, message: '' });
  const [value, setValue] = useState<string>(initialValue ?? '');
  const idInput = `${testid}Input`;
  const idError = `${testid}Error`;
  const idLabel = `${testid}Label`;
  const idDescription = `${testid}Description`;
  const hasError = error.hasError || hasFormError;
  const describedBy =
    `${hasError ? `${idError} ${formErrorId}`.trim() : ''} ${description ? idDescription : ''}`.trim();
  const requiredValidation = useMemo(
    () => errorValidation.find(({ errorKey }) => errorKey === 'required'),
    [errorValidation]
  );

  const validateValue = async (inputValue: string | null, forceValidate?: boolean): Promise<boolean> => {
    let isValid = true;
    const valueToVerify = inputValue || '';

    if (!errorValidation.length) {
      return true;
    }

    await getValidationSchema(errorValidation)
      .validate(valueToVerify)
      .then(() => {
        setError({ hasError: false, message: '' });
        manageForm.removeInputError(name);
      })
      .catch(({ message }) => {
        manageForm.addInputError(name);
        isValid = false;
        if (error.hasError || forceValidate) {
          setError({ hasError: true, message });
        }
      });

    return isValid;
  };

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
      validateValue(initialValue, true);
    }
  }, [initialValue]);

  useEffect(() => {
    manageForm.addInputValidator(name, validateValue);

    return () => {
      manageForm.removeInputValidator(name);
    };
  }, [manageForm, name]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    validateValue(inputValue);
    onChange?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    validateValue(inputValue, true);
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    setValue(initialValue || '');
    onClear?.(event);
  };

  useEffect(() => {
    if (onInputError && error.hasError && error.message) {
      onInputError(name, applyDictionaryToString(dictionary, error.message));
    }
  }, [onInputError, name, error]);

  useEffect(() => {
    if (requiredValidation && hasFormError && value.length === 0) {
      setError({ hasError: true, message: applyDictionaryToString(dictionary, requiredValidation.message) });
      manageForm.addInputError(name);
    }

    return () => {
      manageForm.removeInputError(name);
    };
  }, [hasFormError, requiredValidation?.message, value]);

  return (
    <InputWrapper data-testid={testid}>
      <Label id={idLabel} htmlFor={idInput}>
        {applyDictionaryToString(dictionary, label)}
      </Label>

      {description && <Copy id={idDescription}>{applyDictionaryToString(dictionary, description)}</Copy>}

      <InputInline inlineElement={inlineElement}>
        <DateInput
          name={name}
          value={value}
          width={width}
          setFieldValue={setValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onClear={handleClear}
          format={format}
          required={!!requiredValidation}
          isError={hasError}
          showCalendarIcon={showCalendarIcon}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          {...extraProps}
          id={idInput}
        />
        {hasError && error.message && (
          <Error idError={idError}>{applyDictionaryToString(dictionary, error.message)}</Error>
        )}
      </InputInline>
    </InputWrapper>
  );
};

GenericDateInput.displayName = 'GenericDateInput';
